import { Cell, Row } from '@dexma/ui-components';
import { buildTextInputFieldData } from 'adapter/in/common/form/Text';
import {
    ABSOLUTE_PERCENTAGE_OPTIONS,
    ALIGN_TEXT_OPTIONS,
} from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options';
import {
    StyledOptionContainer,
    StyledResetButton,
    StyledResetOptionContainer,
} from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/Group/style';
import i18n from 'asset/translation';
import OptionsFormField from 'domain/entity/options/optionFormField';
import { WidgetDataTypes } from 'domain/entity/widgetData';
import React, { memo, ReactElement, useEffect, useState } from 'react';
import SelectorValue from 'domain/entity/preset/field/selectorValue';
import { pipe } from 'fp-ts/lib/function';
import { FieldValues, UseFormClearErrors, useFormContext, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { OPTIONS } from 'adapter/in/common/widget/options';
import { ConfigFieldType, FieldData, FieldStatus, FormFieldData, InitialValue, SelectorValueOptions } from 'adapter/in/common/form/types';
import { getDefaultValuesFieldsContainers } from 'adapter/in/common/form/renderer';
import { mapDataToOptionConfigField } from 'adapter/in/common/form/populateForm';
import { buildSelectorFieldData } from 'adapter/in/common/hooks/form/useBaseSelectorContainer';
import { mapFieldGroupOptions } from 'adapter/in/common/form/FieldGroup';

type OptionContainerProps = {
    optionsFormFields: OptionsFormField;
    index: number;
    data: WidgetDataTypes;
};

export const setShowResetByData = (
    fieldKeys: string[],
    data: WidgetDataTypes,
    formValues: FieldData<string, InitialValue>[],
    setShowReset: (check: boolean) => void,
    checked: boolean
): void => {
    const dataValues = fieldKeys.map((fieldKey) => mapDataToOptionConfigField(fieldKey, data));
    const formFieldValues = formValues.map((formValue: FormFieldData) => formValue.getInitialValue());
    const groupWithOptionalValue = !formFieldValues.every((formField) => dataValues.includes(formField.toString()));
    if (!checked || groupWithOptionalValue) {
        const groupFormStatus = formValues.map((fieldValue) => fieldValue.status);
        if (groupFormStatus.every((status) => status === FieldStatus.DEFAULT_DATA)) {
            setShowReset(false);
        } else {
            setShowReset(true);
        }
    }
};

export const handleResetFields = (
    setShowReset: React.Dispatch<React.SetStateAction<boolean>>,
    getValues: UseFormGetValues<FieldValues>,
    setValue: UseFormSetValue<FieldValues>,
    data: WidgetDataTypes,
    fieldKeys: string[],
    clearErrors: UseFormClearErrors<FieldValues>
): void => {
    setShowReset((prevState) => !prevState);
    getValues(fieldKeys).forEach((value: FormFieldData, i: number) => {
        const valueData = mapDataToOptionConfigField(fieldKeys[i], data);
        setValue(fieldKeys[i], getBuilderDefaultDataFieldData(fieldKeys[i] as OPTIONS, value, valueData));
    });
    clearErrors();
};

const getBuilderDefaultDataFieldData = (option: OPTIONS, value: FormFieldData, valueData: string) => {
    switch (option) {
        case `${OPTIONS.ALIGN_TEXT}_header`:
        case `${OPTIONS.ALIGN_TEXT}_body`: {
            const selectorValue = pipe(
                (ALIGN_TEXT_OPTIONS as SelectorValue[]).filter((optValue: SelectorValue) => optValue.id === valueData),
                (values) => mapFieldGroupOptions(values)
            )[0];
            return buildSelectorFieldData(FieldStatus.DEFAULT_DATA, value.getInitialValue().toString(), ConfigFieldType.OPTIONS, ([
                selectorValue,
            ] as unknown) as SelectorValueOptions);
        }
        case OPTIONS.ABSOLUTE_PERCENTAGE: {
            const selectorValue = pipe(
                (ABSOLUTE_PERCENTAGE_OPTIONS as SelectorValue[]).filter((optValue: SelectorValue) => optValue.id === valueData),
                (values) => mapFieldGroupOptions(values)
            )[0];
            return buildSelectorFieldData(FieldStatus.DEFAULT_DATA, value.getInitialValue().toString(), ConfigFieldType.OPTIONS, ([
                selectorValue,
            ] as unknown) as SelectorValueOptions);
        }
        case OPTIONS.CUSTOM_TITLE:
        case OPTIONS.MIN:
        case OPTIONS.MAX:
        default:
            return buildTextInputFieldData(
                FieldStatus.DEFAULT_DATA,
                value.getInitialValue().toString(),
                ConfigFieldType.OPTIONS,
                valueData
            );
    }
};

export const OptionContainer = memo(
    (props: OptionContainerProps): ReactElement => {
        const { optionsFormFields: optionsForm, index, data } = props;
        const { label, fields } = optionsForm;
        const fieldKeys = fields.map((field) => field.key);
        const { getValues, setValue, watch, clearErrors } = useFormContext<FieldValues>();
        const watchedFields = watch(fieldKeys);
        const [showReset, setShowReset] = useState<boolean>(false);

        useEffect(() => {
            if (!watchedFields.includes(undefined) && !watchedFields.map((value) => value.status).includes(FieldStatus.INITIAL_VALUE)) {
                setShowResetByData(fieldKeys, data, watchedFields, setShowReset, showReset);
            }
        }, [fieldKeys, data, showReset, watchedFields]);

        return (
            <>
                {fields.length > 0 && (
                    <StyledOptionContainer key={`${index}`}>
                        <Row style={{ padding: '10px 22px 0px 30px' }}>
                            {label.text !== '' && (
                                <Cell className='widget-type-block'>
                                    <span>{label.text}</span>
                                </Cell>
                            )}
                            {showReset && (
                                <StyledResetOptionContainer>
                                    <StyledResetButton
                                        type='button'
                                        variant='link'
                                        text={i18n.t('widgets.widgetMenu.resetDefaultData')}
                                        data-testid='cancel'
                                        onClick={() => handleResetFields(setShowReset, getValues, setValue, data, fieldKeys, clearErrors)}
                                    />
                                </StyledResetOptionContainer>
                            )}
                        </Row>
                        {getDefaultValuesFieldsContainers(fields)}
                    </StyledOptionContainer>
                )}
            </>
        );
    }
);
